import { Link } from "react-router-dom";
import PageShell from "../components/layout/PageShell";
import {
    getAccountType,
    getSession,
    getUserRole,
    getWorkspaceLabel
} from "../services/authService";

import "./Setting.css";

function SettingsCard({ title, description, items = [], to, badge, tone = "default" }) {
    return (
        <Link to={to} className={`settings-card settings-card-${tone}`}>
            <div className="settings-card-top">
                <div>
                    <span className="settings-card-badge">{badge}</span>
                    <h3>{title}</h3>
                </div>
                <span className="settings-card-arrow">→</span>
            </div>

            <p>{description}</p>

            <div className="settings-card-items">
                {items.map(item => (
                    <span key={item}>{item}</span>
                ))}
            </div>
        </Link>
    );
}

function BillingContextCard({ user }) {
    const billing = user?.billingContext || {};
    const accountType = getAccountType();
    const role = getUserRole();

    return (
        <div className="project-live-card settings-billing-context">
            <span>Billing Context</span>
            <strong>{billing.invoiceProvider || "Shriansh Nexus"}</strong>
            <p>{billing.billingMessage || "Billing and invoices are managed by Shriansh Nexus."}</p>

            <div className="settings-mini-list">
                <small>Account: {accountType || "Client"}</small>
                <small>Role: {role || "AccountOwner"}</small>
                <small>Plan: {user?.billingPlanKey || "trial"}</small>
                <small>Status: {user?.subscriptionStatus || "trialing"}</small>
            </div>
        </div>
    );
}

export default function SettingsPage() {
    const user = getSession() || {};
    const isSuperAdmin = user?.isSuperAdmin === true;
    const accountType = getAccountType();
    const role = getUserRole();

    const isAgency =
        String(accountType).toLowerCase() === "agency" ||
        ["AgencyOwner", "AgencyAdmin", "AgencySupport", "AgencyViewer"]
            .some(x => x.toLowerCase() === String(role).toLowerCase());

    const isAgencyClient =
        String(accountType).toLowerCase() === "client" &&
        !!(user?.parentAccountId || user?.billingContext?.isAgencyClient);

    const workspaceLabel = getWorkspaceLabel();

    return (
        <PageShell title="Settings">
            <div className="settings-shell">
                <section className="project-hero-card">
                    <div>
                        <span className="project-kicker">Workspace Control Center</span>
                        <h1>Settings</h1>
                        <p>
                            Manage your profile, workspace branding, billing visibility, domains,
                            CRM behavior, usage, and account security from one place.
                        </p>

                        <div className="client-settings-summary">
                            <span>{workspaceLabel}</span>
                            <span>{accountType || "Client"}</span>
                            <span>{role || "AccountOwner"}</span>
                        </div>
                    </div>

                    <BillingContextCard user={user} />
                </section>

                <section className="settings-grid">
                    <SettingsCard
                        badge="Account"
                        title="Profile & Login"
                        description="Manage your personal profile, login identity, email, password, and authentication preferences."
                        to="/settings/profile"
                        items={[
                            "Name",
                            "Email",
                            "Password",
                            "Login"
                        ]}
                    />

                    <SettingsCard
                        badge={isAgency ? "Agency" : "Workspace"}
                        title="Workspace & Branding"
                        description={
                            isAgency
                                ? "Control agency brand name, logo, color, support email, and client-facing identity."
                                : "Manage workspace brand, logo, support email, and client-facing identity."
                        }
                        to="/settings/client"
                        items={[
                            "Brand",
                            "Logo",
                            "Support email",
                            "Website"
                        ]}
                    />

                    <SettingsCard
                        badge="Billing"
                        title="Billing & Invoices"
                        description={
                            isAgencyClient
                                ? `Billing is managed by ${user?.billingContext?.billingDisplayName || "your agency"}. View plan context and agency billing contact.`
                                : "Review plan status, billing owner, invoice provider, and future invoice downloads."
                        }
                        to="/settings/usage"
                        tone="billing"
                        items={[
                            user?.billingPlanKey || "Trial",
                            user?.subscriptionStatus || "Trialing",
                            isAgencyClient ? "Agency billing" : "Invoices",
                            "Usage"
                        ]}
                    />

                    <SettingsCard
                        badge="Domains"
                        title="Domains"
                        description="Manage default subdomain, custom domains, DNS verification, SSL status, and published site URLs."
                        to="/settings/domains"
                        items={[
                            "Subdomain",
                            "Custom domain",
                            "DNS",
                            "SSL"
                        ]}
                    />

                    <SettingsCard
                        badge="CRM"
                        title="CRM & Leads"
                        description="Control CRM modules, lead statuses, team access, notification preferences, and module visibility."
                        to="/settings/crm"
                        items={[
                            "Modules",
                            "Lead statuses",
                            "Team",
                            "Notifications"
                        ]}
                    />

                    <SettingsCard
                        badge="Usage"
                        title="Usage & Plan"
                        description="Review project usage, AI usage, deployment activity, CRM content volume, and plan visibility."
                        to="/settings/usage"
                        items={[
                            "Projects",
                            "AI usage",
                            "Deployments",
                            "Plan"
                        ]}
                    />

                    <SettingsCard
                        badge="Security"
                        title="Security"
                        description="Review account access, session behavior, password policy, and sign-in protections."
                        to="/settings/security"
                        items={[
                            "Sessions",
                            "Password",
                            "Access",
                            "Audit"
                        ]}
                    />

                    {isSuperAdmin && (
                        <>
                            <SettingsCard
                                badge="Platform"
                                title="AI & Models"
                                description="Configure AI providers, routing profiles, fallback models, retries, and timeouts."
                                to="/admin/settings/ai"
                                items={[
                                    "Providers",
                                    "Models",
                                    "Routing",
                                    "Fallback"
                                ]}
                            />

                            <SettingsCard
                                badge="Platform"
                                title="Generation & CRM Rules"
                                description="Tune quality thresholds, repair behavior, AI module generation, and duplicate prevention."
                                to="/admin/settings/generation"
                                items={[
                                    "Quality",
                                    "Repair",
                                    "CRM AI",
                                    "Validation"
                                ]}
                            />

                            <SettingsCard
                                badge="Platform"
                                title="Deployment & Privacy"
                                description="Manage versioning, rollback, cache busting, audit visibility, and privacy boundaries."
                                to="/admin/settings/platform"
                                items={[
                                    "Versions",
                                    "Rollback",
                                    "Audit",
                                    "Privacy"
                                ]}
                            />
                        </>
                    )}
                </section>
            </div>
        </PageShell>
    );
}